import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, List, CalendarDays } from "lucide-react";
import { addMonths, addWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, isSameMonth, isToday } from "date-fns";
import { useLang } from "@/lib/i18n";
import { fmtDate, fmtTime } from "@/lib/format";

const shiftCls = (a) => {
  if (a.status === "cancelled" || a.status === "declined" || a.event.status === "cancelled") return "border-neutral-200 text-neutral-400 line-through";
  return a.status === "confirmed" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800";
};
const OPEN_CLS = "border-dashed border-[#C5A059]/70 text-[#D9B872] bg-[#C5A059]/[0.06]";
const AVAIL_MARK = { available: "✓", maybe: "?", unavailable: "✕" };

const Pill = ({ entry, onOpen, big }) => {
  const { t } = useLang();
  const { kind, x } = entry;
  const ev = kind === "shift" ? x.event : x;
  const time = kind === "shift" ? x.slot.start_time || ev.start_time : ev.start_time;
  return (
    <button data-testid={kind === "shift" ? `shift-calendar-item-${x.id}` : `shift-calendar-open-${x.id}`} onClick={() => onOpen(entry)} title={ev.name}
      className={`press w-full text-left border rounded-md px-1 py-0.5 sm:px-1.5 sm:py-1 text-[9px] sm:text-[11px] leading-tight truncate hover:border-[#C5A059] ${kind === "shift" ? shiftCls(x) : OPEN_CLS} ${big ? "!py-2 !px-2.5 !text-xs" : ""}`}>
      {kind === "event" && x.my_availability && <span className="mono mr-1 font-semibold">{AVAIL_MARK[x.my_availability]}</span>}
      <span className={`mono mr-1 ${big ? "" : "hidden sm:inline"}`}>{fmtTime(time, t)}</span>{ev.name}
    </button>
  );
};

export const ViewSwitch = ({ view, setView }) => {
  const { t } = useLang();
  return (
    <div className="inline-flex rounded-full border border-white/10 p-1 text-xs" data-testid="schedule-view-switch">
      {[["list", List, "p.viewList"], ["calendar", CalendarDays, "p.viewCalendar"]].map(([v, Icon, k]) => (
        <button key={v} data-testid={`schedule-view-${v}`} onClick={() => setView(v)}
          className={`press flex items-center gap-1.5 rounded-full px-4 py-2 ${view === v ? "bg-[#C5A059] text-[#0A0A0C] font-semibold" : "text-neutral-500 hover:text-black"}`}>
          <Icon className="w-3.5 h-3.5" />{t(k)}
        </button>
      ))}
    </div>
  );
};

const Legend = ({ shifts, open }) => {
  const { t } = useLang();
  if (!shifts && !open) {
    return null;
  }
  const dot = (cls, label) => <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-sm border ${cls}`} />{label}</span>;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 px-4 py-3 border-t border-black/[0.06] text-[11px] text-neutral-500" data-testid="shift-calendar-legend">
      {shifts && dot("border-emerald-200 bg-emerald-50", t("as.confirmed"))}
      {shifts && dot("border-amber-200 bg-amber-50", t("as.pending"))}
      {open && dot(OPEN_CLS, t("cal.openEvent"))}
    </div>
  );
};

export const ShiftCalendar = ({ items = [], events = [], onOpen }) => {
  const { t, lang } = useLang();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const byDate = useMemo(() => {
    const m = {};
    const add = (date, entry) => { (m[date] = m[date] || []).push(entry); };
    (items || []).forEach((a) => add(a.event.date, { kind: "shift", x: a }));
    (events || []).filter((e) => !e.assigned).forEach((e) => add(e.date, { kind: "event", x: e }));
    return m;
  }, [items, events]);
  const days = view === "month"
    ? eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }) })
    : eachDayOfInterval({ start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) });
  const iso = (d) => format(d, "yyyy-MM-dd");
  const shift = (n) => setCursor(view === "month" ? addMonths(cursor, n) : addWeeks(cursor, n));
  const title = view === "month" ? fmtDate(iso(cursor), lang, "MMMM yyyy") : `${fmtDate(iso(days[0]), lang, "d MMM")} – ${fmtDate(iso(days[6]), lang, "d MMM")}`;
  const navBtn = "press p-1.5 rounded-full border border-black/10 hover:border-[#C5A059]";
  const key = (en) => `${en.kind}-${en.x.id}`;

  return (
    <div className="card-lux overflow-hidden rise" data-testid="shift-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 border-b border-black/[0.06]">
        <div className="flex items-center gap-2 min-w-0">
          <button data-testid="shift-calendar-prev" onClick={() => shift(-1)} className={navBtn}><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="shift-calendar-next" onClick={() => shift(1)} className={navBtn}><ChevronRight className="w-4 h-4" /></button>
          <h3 className="font-display text-xl sm:text-2xl capitalize ml-1 truncate" data-testid="shift-calendar-title">{title}</h3>
          <button data-testid="shift-calendar-today" onClick={() => setCursor(new Date())} className="press text-xs text-neutral-500 hover:text-black underline underline-offset-4">{t("dash.today")}</button>
        </div>
        <div className="inline-flex rounded-full border border-white/10 p-0.5 text-xs">
          {["month", "week"].map((v) => (
            <button key={v} data-testid={`shift-calendar-view-${v}`} onClick={() => setView(v)} className={`press rounded-full px-3 py-1.5 ${view === v ? "bg-[#C5A059] text-[#0A0A0C] font-semibold" : "text-neutral-500 hover:text-black"}`}>{t(`dash.${v}`)}</button>
          ))}
        </div>
      </div>
      {view === "month" ? (
        <div className="grid grid-cols-7">
          {days.slice(0, 7).map((d) => <div key={`h${d}`} className="eyebrow !text-[9px] sm:!text-[10px] px-1 sm:px-2 py-2 border-b border-black/[0.06] truncate">{fmtDate(iso(d), lang, "EEE").replace(".", "")}</div>)}
          {days.map((d) => {
            const list = byDate[iso(d)] || [];
            return (
              <div key={iso(d)} data-testid={`shift-calendar-day-${iso(d)}`} className={`min-w-0 border-b border-r border-black/[0.05] p-1 sm:p-1.5 min-h-[72px] sm:min-h-[100px] ${!isSameMonth(d, cursor) ? "opacity-40" : ""}`}>
                <div className={`mono text-[10px] sm:text-[11px] mb-1 ${isToday(d) ? "inline-grid place-items-center w-5 h-5 bg-[#C5A059] text-[#0A0A0C] rounded-full" : "text-neutral-400"}`}>{format(d, "d")}</div>
                <div className="space-y-1">
                  {list.slice(0, 2).map((en) => <Pill key={key(en)} entry={en} onOpen={onOpen} />)}
                  {list.length > 2 && <div className="text-[9px] text-neutral-500 px-0.5">+{list.length - 2}</div>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="divide-y divide-black/[0.05]">
          {days.map((d) => {
            const list = byDate[iso(d)] || [];
            return (
              <div key={iso(d)} data-testid={`shift-calendar-day-${iso(d)}`} className="flex gap-4 px-4 py-3">
                <div className={`w-14 shrink-0 ${isToday(d) ? "text-[#C5A059]" : ""}`}>
                  <div className="eyebrow capitalize">{fmtDate(iso(d), lang, "EEE").replace(".", "")}</div>
                  <div className="font-display text-2xl leading-none mt-1">{format(d, "d")}</div>
                </div>
                <div className="flex-1 min-w-0 space-y-1.5 self-center">
                  {list.length === 0 ? <span className="text-xs text-neutral-400">—</span> : list.map((en) => <Pill key={key(en)} entry={en} onOpen={onOpen} big />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Legend shifts={(items || []).length > 0} open={(events || []).length > 0} />
    </div>
  );
};
